import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Delete, Download, Visibility } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import CustomDialog from '../../../layout/CustomDialog';
import Input from '../../../form/Input';
import Checkbox from '../../../form/Checkbox';
import { getApiUrl } from '../../../../utils/retornaTratamentoURL';

function FormComprovantes({
    dados,
    itens,
    novoComprovante,
    preview,
    inputFileRef,

    isLoading,
    isSubmitting,
    openConfirmDelete,

    handleFileChange,
    handleChange,
    handleSalvar,
    handlePrepararExclusao,
    handleConfirmDelete,
    handleCloseDelete,
    limparComprovante,

    onClose,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [visualizacao, setVisualizacao] = useState(null);
    const [baixando, setBaixando] = useState(false);
    const [erroDownload, setErroDownload] = useState('');
    const downloadRef = useRef(null);
    useEffect(() => () => { downloadRef.current?.abort(); }, []);

    const arquivoInfo = (item) => {
        const nome = typeof item?.arquivo === 'string' ? item.arquivo : '';
        const isPdf = /\.pdf$/i.test(nome);
        return {
            arquivo: nome,
            nome: item?.nomeOriginal || nome,
            url: nome ? getApiUrl(`${isPdf ? 'pdf' : 'imagens'}/${nome}`) : '',
            isPdf,
            isImagem: /\.(png|jpe?g|gif|webp|bmp|avif)$/i.test(nome),
        };
    };

    const baixar = async (item) => {
        const arquivo = arquivoInfo(item);
        if (!arquivo.url || downloadRef.current) return;
        const controller = new AbortController();
        downloadRef.current = controller;
        setBaixando(true);
        setErroDownload('');
        try {
            const response = await fetch(arquivo.url, { signal: controller.signal });
            if (!response.ok) throw new Error(`Não foi possível baixar o arquivo (HTTP ${response.status}).`);
            const blob = await response.blob();
            if (controller.signal.aborted) return;
            if (blob.type.includes('text/html')) throw new Error('O servidor retornou uma página em vez do arquivo.');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            try {
                link.href = url;
                link.download = arquivo.nome || 'documento';
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }
        } catch (error) {
            if (!controller.signal.aborted) setErroDownload(error.message || 'Não foi possível baixar o arquivo.');
        } finally {
            if (downloadRef.current === controller) downloadRef.current = null;
            if (!controller.signal.aborted) setBaixando(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                id: 'Comprovantes',
                header: 'Comprovantes',
                columns: [
                    {
                        accessorKey: 'arquivo',
                        header: 'Arquivo',
                        Cell: ({ row }) => (
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                                {row.original.nomeOriginal || row.original.arquivo || 'Comprovante sem arquivo'}
                            </Typography>
                        ),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ row }) => (row.original?.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    const podeSalvar = !!novoComprovante?.arquivo?.length && !isSubmitting;

    return (
        <>
            {erroDownload && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroDownload('')}>{erroDownload}</Alert>}
            {openConfirmDelete && (
                <CustomDialog
                    title="Excluir comprovante"
                    content="Você tem certeza?"
                    onClose={handleCloseDelete}
                    onConfirm={handleConfirmDelete}
                />
            )}

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ mb: 2 }}
            >
                <Typography variant="body1">
                    <strong>Total de comprovantes:</strong> {Array.isArray(itens) ? itens.length : 0}
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Button variant="contained" color="secondary" onClick={onClose}>
                        Voltar
                    </Button>
                </Stack>
            </Stack>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '1rem',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                }}
            >
                <Input
                    ref={inputFileRef}
                    text="PDF do comprovante"
                    type="file"
                    name="novoComprovante.arquivo"
                    handleOnChange={handleFileChange}
                    inputWidth="400px"
                    multiple={true}
                    accept="application/pdf"
                />

                <Checkbox
                    title="Ativo quando marcado"
                    text="Ativo"
                    name="ativo"
                    handleOnChange={handleChange}
                    value={novoComprovante.ativo}
                    checked={!!novoComprovante.ativo}
                />

                <Button variant="contained" color="inherit" onClick={limparComprovante}>
                    Limpar
                </Button>

                <Button
                    variant="contained"
                    color={novoComprovante.funcao?.toLowerCase() === 'deletar' ? 'error' : 'info'}
                    onClick={handleSalvar}
                    disabled={!podeSalvar}
                >
                    {novoComprovante.funcao || 'Adicionar'}
                </Button>
            </Box>

            {!!preview?.length && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Arquivos selecionados:</strong> {preview.map((f) => f.name).join(', ')}
                    </Typography>
                </Box>
            )}

            <MaterialReactTable
                columns={columns}
                data={Array.isArray(itens) ? itens : []}
                displayColumnDefOptions={{ 'mrt-row-actions': { size: 150 } }}
                enableRowActions
                getRowId={(row) => row.uuid}
                localization={MRT_Localization_PT_BR}
                initialState={{
                    showColumnFilters: true,
                    density: 'compact',
                }}
                state={{
                    isLoading,
                }}
                renderRowActions={({ row }) => (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton title="Visualizar comprovante" aria-label="Visualizar comprovante" disabled={!row.original.arquivo}
                            onClick={() => setVisualizacao(arquivoInfo(row.original))}><Visibility /></IconButton>
                        <IconButton title="Baixar comprovante" aria-label="Baixar comprovante" disabled={!row.original.arquivo || baixando}
                            onClick={() => baixar(row.original)}><Download /></IconButton>
                        <Tooltip arrow placement="right" title="Deletar">
                            <span>
                                <IconButton
                                    color="error"
                                    onClick={() => handlePrepararExclusao(row.original)}
                                    disabled={isSubmitting}
                                >
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
                renderDetailPanel={({ row }) => (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 1 }}>
                        <Button startIcon={<Visibility />} title="Visualizar o comprovante nesta janela"
                            disabled={!row.original.arquivo} onClick={() => setVisualizacao(arquivoInfo(row.original))}>Visualizar</Button>
                        <Button startIcon={<Download />} title="Baixar o arquivo do comprovante"
                            disabled={!row.original.arquivo || baixando} onClick={() => baixar(row.original)}>Baixar</Button>
                    </Stack>
                )}
                positionExpandColumn="first"
                positionActionsColumn="first"
                positionToolbarAlertBanner="bottom"
            />
            <Dialog open={Boolean(visualizacao)} onClose={() => setVisualizacao(null)} fullWidth maxWidth="lg" fullScreen={isMobile}>
                <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{visualizacao?.nome || 'Documento'}</DialogTitle>
                {erroDownload && <Alert severity="error">{erroDownload}</Alert>}
                <DialogContent sx={{ height: '75vh', p: 1 }}>
                    {visualizacao?.isPdf ? (
                        <Box
                            component="iframe"
                            title="Visualização do documento"
                            src={visualizacao.url}
                            sx={{ border: 0, width: '100%', height: '100%' }}
                        />
                    ) : visualizacao?.isImagem ? (
                        <Box
                            component="img"
                            src={visualizacao.url}
                            alt={visualizacao.nome}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Alert severity="info">Visualização indisponível para este formato. Utilize Baixar.</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap' }}>
                    <Button startIcon={<Download />} title="Baixar comprovante" disabled={baixando || !visualizacao?.url}
                        onClick={() => baixar({ arquivo: visualizacao?.arquivo, nomeOriginal: visualizacao?.nome })}>Baixar</Button>
                    {visualizacao?.url && (
                        <Button component="a" href={visualizacao.url} target="_blank" rel="noopener noreferrer">
                            Abrir em outra aba
                        </Button>
                    )}
                    <Button type="button" onClick={() => setVisualizacao(null)}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default FormComprovantes;
